import { Ref, unref } from 'vue'
import { v4 } from 'uuid'

export type TreeItem = {
  id: string
  parentId?: string
}
export type TreeItemNode<I extends TreeItem> = {
  id: string
  item: I
  children: TreeItemNode<I>[]
}

const sortNodes = <N extends { children: unknown[] }>(a: N, b: N) => {
  const aHasChildren = a.children.length > 0
  const bHasChildren = b.children.length > 0
  if (aHasChildren && !bHasChildren) {
    return -1
  }
  if (bHasChildren && !aHasChildren) {
    return 1
  }
  return 0
}
const createNode = <I extends TreeItem>(
  itemsByParentId: Map<string, I[]>,
  item: I,
  shouldSort: boolean = true,
): TreeItemNode<I> => {
  const unsortedChildren = (itemsByParentId.get(item.id) ?? []).map((i) =>
    createNode(itemsByParentId, i, shouldSort),
  )

  return {
    id: v4(),
    item,
    children: shouldSort ? unsortedChildren.toSorted(sortNodes) : unsortedChildren,
  }
}
const rootNodes = <I extends TreeItem>(
  items: Ref<I[]> | I[],
  shouldSort: boolean = true,
): TreeItemNode<I>[] => {
  const rawItems = unref(items)
  const itemsByParentId = new Map<string, I[]>()
  for (const item of rawItems) {
    if (!item.parentId) continue
    const siblings = itemsByParentId.get(item.parentId)
    if (siblings) siblings.push(item)
    else itemsByParentId.set(item.parentId, [item])
  }
  const roots = rawItems
    .filter((i) => !i.parentId)
    .map((i) => createNode(itemsByParentId, i, shouldSort))

  return shouldSort ? roots.toSorted(sortNodes) : roots
}

const recursiveItemIds = <I extends TreeItem, N extends TreeItemNode<I>>(
  node: N,
  itemIds: string[] = [],
) => {
  itemIds.push(node.item.id)
  node.children.forEach((c) => recursiveItemIds(c, itemIds))
  return itemIds
}

const traverseNodeForItemId = <I extends TreeItem, N extends TreeItemNode<I>>(
  acc: string[],
  node: N,
  itemId: string,
) => {
  const isTargetNode = node.item.id == itemId
  const childHasTargetNode = node.children.some((c) => traverseNodeForItemId(acc, c, itemId))
  if (isTargetNode || childHasTargetNode) {
    acc.push(node.item.id)
    return true
  }

  return false
}
const itemIdsPathTo = <I extends TreeItem, N extends TreeItemNode<I>>(
  nodes: N[],
  itemId: string,
) => {
  const accumulator: string[] = []
  nodes.forEach((n) => traverseNodeForItemId(accumulator, n, itemId))
  return accumulator
}

type IdMap<P> = {
  [key: string]: P
}
const mapItemIdsTo = <I extends TreeItem, N extends TreeItemNode<I>, P>(
  nodes: N[],
  extractor: (item: TreeItemNode<I>) => P,
  accumulator: IdMap<P> = {},
) => {
  nodes.forEach((n) => {
    accumulator[n.item.id] = extractor(n)
    n.children.forEach((c) => mapItemIdsTo<I, TreeItemNode<I>, P>([c], extractor, accumulator))
  })
  return accumulator
}

export default function useTree() {
  return {
    treeRootNodes: rootNodes,
    treeRecursiveItemIds: recursiveItemIds,
    treeItemIdsPathTo: itemIdsPathTo,
    treeMapItemIdsTo: mapItemIdsTo,
  }
}
